import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { SlidersHorizontal } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDateTime, fmtMoney } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { PageHeader, ToneChip } from "../../components/common";
import { Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function AdjustDialog({ refs, open, onClose, onDone }) {
  const [form, setForm] = useState({ code: "", amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finanzas/budget/adjust", { ...form, amount: Number(form.amount) });
      toast.success("Ajuste presupuestario registrado con trazabilidad");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="budget-adjust-dialog">
        <DialogHeader><DialogTitle>Ajuste presupuestario</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Partida *</Label>
            <Select value={form.code} onValueChange={(v) => setForm({ ...form, code: v })}>
              <SelectTrigger data-testid="adjust-code"><SelectValue placeholder="Selecciona partida…" /></SelectTrigger>
              <SelectContent>{refs.map((r) => <SelectItem key={r.code} value={r.code}>{r.code} — {r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Importe (€, negativo para reducir) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adjust-amount" /></div>
          <div><Label>Nota explicativa *</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="adjust-note" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || !form.code || !Number(form.amount) || !form.note.trim()} data-testid="adjust-submit">
            {busy ? "Aplicando…" : "Aplicar ajuste"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Presupuesto() {
  const { user } = useAuth();
  const { map } = useUsers();
  const [open, setOpen] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["fin-budget"],
    queryFn: async () => (await api.get("/finanzas/budget")).data,
  });
  if (!data) return null;
  const { references, movements } = data;
  const canAdjust = (user?.roles || []).some((r) => ["APPROVER", "AREA_MANAGER", "EXECUTIVE", "DEMO_ADMIN"].includes(r));

  return (
    <div data-testid="presupuesto-page">
      <PageHeader
        title="Control presupuestario"
        subtitle="Reserva → compromiso → facturado → pagado, con movimientos trazables por partida."
        actions={canAdjust && (
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="budget-adjust-button">
            <SlidersHorizontal className="mr-1 h-4 w-4" /> Ajuste presupuestario
          </Button>
        )}
      />
      <div className="ts-card overflow-hidden" data-testid="budget-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Partida</TableHead><TableHead>Área</TableHead><TableHead className="text-right">Presupuesto</TableHead>
              <TableHead className="text-right">Comprometido</TableHead><TableHead className="text-right">Reservado</TableHead>
              <TableHead className="text-right">Facturado</TableHead><TableHead className="text-right">Pagado</TableHead>
              <TableHead className="text-right">Disponible</TableHead><TableHead className="w-40">Consumo</TableHead><TableHead className="text-right">Forecast</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {references.map((r) => (
              <TableRow key={r.code} data-testid={`budget-row-${r.code}`} className={r.available < 0 ? "bg-red-50/50" : ""}>
                <TableCell>
                  <p className="font-mono text-xs font-semibold">{r.code}</p>
                  <p className="max-w-52 truncate text-xs text-slate-500">{r.name}</p>
                </TableCell>
                <TableCell className="text-xs">{r.area}</TableCell>
                <TableCell className="text-right"><Money value={r.budget} /></TableCell>
                <TableCell className="text-right"><Money value={r.committed} /></TableCell>
                <TableCell className="text-right"><Money value={r.reserved} /></TableCell>
                <TableCell className="text-right"><Money value={r.invoiced} /></TableCell>
                <TableCell className="text-right"><Money value={r.paid} /></TableCell>
                <TableCell className={`text-right font-mono text-sm font-semibold ${r.available < 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`budget-available-${r.code}`}>
                  {fmtMoney(r.available)}
                </TableCell>
                <TableCell>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full ${r.pct_committed > 90 ? "bg-red-500" : r.pct_committed > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, r.pct_committed)}%` }} />
                  </div>
                  <p className="mt-0.5 text-right font-mono text-[10px] text-slate-400">{r.pct_committed}%</p>
                </TableCell>
                <TableCell className="text-right">
                  {r.forecast_deviation > 0
                    ? <ToneChip label={`+${fmtMoney(r.forecast_deviation)}`} tone="danger" testid={`budget-dev-${r.code}`} />
                    : <span className="font-mono text-xs text-slate-400">{fmtMoney(r.forecast)}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <h2 className="ts-section-title mb-3 mt-8">Movimientos recientes</h2>
      <div className="ts-card overflow-hidden" data-testid="budget-movements">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Fecha</TableHead><TableHead>Partida</TableHead><TableHead>Tipo</TableHead><TableHead>Nota</TableHead><TableHead>Por</TableHead><TableHead className="text-right">Importe</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {movements.map((m) => (
              <TableRow key={m.id} data-testid={`movement-${m.id}`}>
                <TableCell className="text-xs">{fmtDateTime(m.at)}</TableCell>
                <TableCell className="font-mono text-xs">{m.budget_reference}</TableCell>
                <TableCell><ToneChip label={m.kind} tone={m.kind === "liberacion" || m.kind === "abono" ? "muted" : m.kind === "pagado" ? "success" : "info"} /></TableCell>
                <TableCell className="max-w-72 truncate text-xs">{m.note}</TableCell>
                <TableCell className="text-xs">{userName(map, m.by)}</TableCell>
                <TableCell className="text-right"><Money value={m.amount} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AdjustDialog refs={references} open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); refetch(); }} />
    </div>
  );
}
